/**
 * Connexion par numéro + code WhatsApp — logique des consoles web (sans React).
 *
 * Partagée par les cinq consoles (dispatch, marchand, opérateur, portail SaaS, supervision) : chacune lui passe
 * SON client Supabase. Les règles de numéro (table des pays, E.164) viennent du module pur des edge functions :
 * `supabase/functions/_shared/otp-whatsapp.ts` — une seule définition, jamais recopiée.
 *
 * Le code est généré, stocké (haché), expiré et limité en essais par Supabase Auth ; l'edge function
 * `envoyer-code-whatsapp` ne fait que le livrer par WhatsApp.
 */
import type { SupabaseClient } from '@supabase/supabase-js'
import {
  PAYS,
  PAYS_PAR_DEFAUT,
  normaliserTelephone,
  paysParDefaut,
  trouverPays,
  type Pays,
  type ResultatNumero,
} from '../../SwiftEats/supabase/functions/_shared/otp-whatsapp'

export { PAYS, PAYS_PAR_DEFAUT, normaliserTelephone, paysParDefaut, trouverPays }
export type { Pays, ResultatNumero }

/** Délai avant de pouvoir redemander un code (aligné sur `max_frequency` = 60 s de la configuration Auth). */
export const SECONDES_AVANT_RENVOI = 60
/** Durée de vie d'un code (10 minutes) : au-delà, « code expiré » plutôt que « code incorrect ». */
export const SECONDES_VALIDITE_CODE = 600
export const LONGUEUR_CODE = 6

const CLE_PAYS = 'cw.pays'

/** Dernier pays choisi sur cet appareil (confort ; jamais indispensable : le stockage peut être bloqué). */
export function paysMemorise(): string | null {
  try {
    return trouverPays(window.localStorage.getItem(CLE_PAYS))?.iso ?? null
  } catch {
    return null
  }
}

export function memoriserPays(iso: string): void {
  try {
    window.localStorage.setItem(CLE_PAYS, iso)
  } catch { /* stockage bloqué : sans conséquence */ }
}

/**
 * Pays proposé d'office. Pour une console, la RÉGION DU NAVIGATEUR n'est PAS consultée (un ordinateur de Bamako
 * réglé en « fr-FR » proposerait la France) : mémoire de l'appareil, sinon pays de configuration (`pays_defaut()`),
 * sinon Mali. Les apps natives, elles, tiennent compte de la région de l'appareil.
 */
export async function lirePaysDefaut(client: SupabaseClient): Promise<Pays> {
  const memo = paysMemorise()
  if (memo) return trouverPays(memo)!
  try {
    const { data } = await client.rpc('pays_defaut')
    if (typeof data === 'string') return paysParDefaut(data, null)
  } catch { /* migration pas encore poussée */ }
  return paysParDefaut(null, null)
}

/* ------------------------------------------------------------------ erreurs */

export type CodeErreurWhatsapp =
  | 'code_expire' | 'code_incorrect' | 'trop_de_tentatives' | 'numero_invalide' | 'whatsapp_indisponible'
  | 'inscription_fermee' | 'compte_introuvable' | 'reseau' | 'autre'

export interface ErreurWhatsapp { code: CodeErreurWhatsapp; message: string; attenteSecondes?: number }

/**
 * Erreur Supabase Auth (anglais) ou du hook (français, contient « WhatsApp ») → phrase française.
 * `secondesDepuisEnvoi` : temps écoulé depuis l'envoi du code — GoTrue répond la MÊME phrase pour un code faux et
 * un code périmé (« Token has expired or is invalid »), c'est donc notre horloge qui les distingue.
 */
export function traduireErreurWhatsapp(err: unknown, secondesDepuisEnvoi?: number): ErreurWhatsapp {
  const brut = err instanceof Error ? err.message : typeof err === 'string' ? err : JSON.stringify(err ?? '')
  const t = brut.toLowerCase()
  const codeErr = String((err as { code?: unknown } | null)?.code ?? '').toLowerCase()
  const statut = Number((err as { status?: unknown } | null)?.status ?? 0)
  const tout = `${t} ${codeErr}`

  // Phrase déjà française de l'edge function (elle contient toujours « WhatsApp ») : telle quelle.
  if (/whatsapp/i.test(brut) && /[àâçéèêëîïôûùü]|utilisez|réessayez|patientez/i.test(brut)) {
    const limite = /trop de codes|patientez/i.test(brut)
    return { code: limite ? 'trop_de_tentatives' : 'whatsapp_indisponible', message: nettoyer(brut) }
  }
  if (/over_sms_send_rate_limit|over_request_rate_limit|rate limit|too many|only request this after|for security purposes/.test(tout) || statut === 429) {
    const m = /after (\d+) second/.exec(t)
    const attente = m ? Number(m[1]) : undefined
    return {
      code: 'trop_de_tentatives',
      message: attente
        ? `Trop de demandes. Réessayez dans ${attente} seconde${attente > 1 ? 's' : ''}.`
        : 'Trop de tentatives. Patientez quelques minutes, puis réessayez.',
      ...(attente ? { attenteSecondes: attente } : {}),
    }
  }
  if (/otp_expired|token has expired|expired or is invalid|invalid token|otp.*(invalid|expired)/.test(tout)) {
    return (secondesDepuisEnvoi ?? 0) > SECONDES_VALIDITE_CODE
      ? { code: 'code_expire', message: 'Ce code a expiré. Demandez un nouveau code.' }
      : { code: 'code_incorrect', message: 'Code incorrect ou expiré. Vérifiez les 6 chiffres reçus sur WhatsApp, ou demandez un nouveau code.' }
  }
  if (/signups? not allowed|user_not_found|otp_disabled|signup_disabled/.test(tout)) {
    return { code: 'compte_introuvable', message: 'Aucun compte avec ce numéro. Créez votre compte ou utilisez votre adresse e-mail.' }
  }
  if (/phone.*(disabled|provider)|unsupported phone provider|sms_send_failed|error sending|hook/.test(tout)) {
    return { code: 'whatsapp_indisponible', message: "L'envoi du code par WhatsApp n'est pas disponible pour le moment. Utilisez votre adresse e-mail." }
  }
  if (/invalid.*phone|phone.*invalid|validation_failed|unable to validate phone/.test(tout)) {
    return { code: 'numero_invalide', message: "Ce numéro n'est pas valide." }
  }
  if (/failed to fetch|networkerror|network request failed|load failed|timeout|offline/.test(tout)) {
    return { code: 'reseau', message: 'Pas de connexion. Vérifiez votre réseau et réessayez.' }
  }
  return { code: 'autre', message: `Connexion impossible : ${brut || 'erreur inconnue'}` }
}

function nettoyer(s: string): string {
  return s.replace(/^(authapierror|authretryablefetcherror):?\s*/i, '').trim()
}

/* ------------------------------------------------------------------ appels */

export interface OptionsDemande { creerSiInconnu?: boolean }

/** Demande l'envoi d'un code WhatsApp. `e164` : « +22370123456 » (sortie de `normaliserTelephone`). */
export async function demanderCodeWhatsapp(client: SupabaseClient, e164: string, options: OptionsDemande = {}): Promise<void> {
  const { error } = await client.auth.signInWithOtp({
    phone: e164,
    options: { shouldCreateUser: options.creerSiInconnu === true },
  })
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code, status: (error as { status?: number }).status })
}

/** Vérifie le code et ouvre la session. Renvoie l'identifiant de l'utilisateur. */
export async function verifierCodeWhatsapp(client: SupabaseClient, e164: string, code: string): Promise<string> {
  const { data, error } = await client.auth.verifyOtp({ phone: e164, token: code.trim(), type: 'sms' })
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code, status: (error as { status?: number }).status })
  if (!data.user) throw new Error('Connexion refusée.')
  return data.user.id
}

/** Ajoute un numéro à un compte connecté (e-mail), puis `verifierAjoutNumero` avec le code reçu. */
export async function ajouterNumero(client: SupabaseClient, e164: string): Promise<void> {
  const { error } = await client.auth.updateUser({ phone: e164 })
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code, status: (error as { status?: number }).status })
}

export async function verifierAjoutNumero(client: SupabaseClient, e164: string, code: string): Promise<void> {
  const { error } = await client.auth.verifyOtp({ phone: e164, token: code.trim(), type: 'phone_change' })
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code, status: (error as { status?: number }).status })
}

/** Ajoute un e-mail à un compte connecté par numéro : Supabase envoie un lien de confirmation. */
export async function ajouterEmail(client: SupabaseClient, email: string): Promise<void> {
  const { error } = await client.auth.updateUser({ email: email.trim() })
  if (error) throw Object.assign(new Error(error.message), { code: (error as { code?: string }).code, status: (error as { status?: number }).status })
}

/* ------------------------------------------------------------------ saisie du code */

/** Garde les seuls chiffres (collage « 482 913 », « 482-913 », code copié avec des espaces) et coupe à 6. */
export function nettoyerCode(saisie: string): string {
  return saisie.replace(/\D+/g, '').slice(0, LONGUEUR_CODE)
}

/** Secondes restantes avant de pouvoir redemander un code (jamais négatif). */
export function secondesRestantes(envoyeLe: number, maintenant: number, delai = SECONDES_AVANT_RENVOI): number {
  return Math.max(0, Math.ceil(delai - (maintenant - envoyeLe) / 1000))
}

/** Texte du compte à rebours : « Renvoyer le code dans 42 s » ou « Renvoyer le code ». */
export function libelleRenvoi(restant: number): string {
  return restant > 0 ? `Renvoyer le code dans ${restant} s` : 'Renvoyer le code'
}

/**
 * Ce que la console affiche après la création d'un compte par le dispatch / l'opérateur : les accès à remettre.
 * `connexion` vient de l'edge function (`mot_de_passe` | `whatsapp` | `existant`).
 */
export function resumeAcces(r: {
  email?: string | null; phone?: string | null; temp_password?: string | null; connexion?: string | null
}): { identifiant: string; secret: string | null; phrase: string } {
  const identifiant = r.email || r.phone || ''
  if (r.temp_password) return { identifiant, secret: r.temp_password, phrase: 'Mot de passe temporaire (à transmettre, affiché une seule fois)' }
  if (r.connexion === 'whatsapp' || (!r.email && r.phone)) {
    return { identifiant: r.phone || identifiant, secret: null, phrase: 'La personne se connecte avec son numéro WhatsApp : elle reçoit un code à chaque connexion.' }
  }
  return { identifiant, secret: null, phrase: 'Compte déjà existant : la personne garde son mot de passe (ou son numéro WhatsApp).' }
}
